import React from 'react';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Brandmark } from '@uhg-abyss/web/ui/Brandmark';
import { Footer as AbyssFooter } from '@uhg-abyss/web/ui/Footer';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Router } from '@uhg-abyss/web/ui/Router';
import { V1SubNavigationMenu } from '@uhg-abyss/web/ui/SubNavigationMenu';
import { Text } from '@uhg-abyss/web/ui/Text';
import { Tooltip } from '@uhg-abyss/web/ui/Tooltip';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { useAuth } from '../../auth';
import { navigate } from '../navigate';
import { subscribeSideNavCollapse } from '../sideNavController';
import { UI_KEYS } from '../uiKeys';

const HEADER_HEIGHT = '72px';
const FOOTER_HEIGHT = '36px';
const SIDENAV_WIDTH = 'clamp(164px, 15vw, 200px)';
const SIDENAV_COLLAPSED_WIDTH = '50px';
const SIDENAV_HIDE_DELAY_MS = 150;
const SIDENAV_SHOW_DELAY_MS = 80;

const BASE_SIDE_NAV_ITEMS = [
    {
        title: 'Home',
        href: '/home',
        icon: 'home',
        before: <IconSymbol icon="home" size={18} />,
    },
    {
        title: 'Prior Years RFI',
        href: '/historicdata',
        icon: 'history',
        before: <IconSymbol icon="history" size={18} />,
    },
    {
        title: 'Reports',
        href: '/reports',
        icon: 'bar_chart',
        before: <IconSymbol icon="bar_chart" size={18} />,
    },
    {
        title: 'RFI Help',
        href: '/rfiinfo',
        icon: 'help',
        before: <IconSymbol icon="help" size={18} />,
    },
];

const ADMIN_SIDE_NAV_ITEM = {
    title: 'Admin',
    icon: 'admin_panel_settings',
    before: <IconSymbol icon="admin_panel_settings" size={18} />,
    subItems: [
        {
            title: 'Surveys',
            href: '/admin/surveys',
            icon: 'assignment',
            before: <IconSymbol icon="assignment" size={18} />,
        },
        {
            title: 'Questions',
            href: '/admin/questions',
            icon: 'quiz',
            before: <IconSymbol icon="quiz" size={18} />,
        },
        {
            title: 'Batch Jobs',
            href: '/admin/udw-batch-jobs',
            icon: 'sync_alt',
            before: <IconSymbol icon="sync_alt" size={18} />,
        },
    ],
};

const Shell = styled('div', {
    height: '100vh',
    width: '100%',
    backgroundColor: '$white',
    overflow: 'hidden',
});

const Header = styled('header', {
    width: '100%',
    height: HEADER_HEIGHT,
    maxHeight: HEADER_HEIGHT,
    display: 'grid',
    gridTemplateColumns: 'minmax(96px, 20vw) minmax(0, 1fr) minmax(140px, 26vw)',
    columnGap: '0.75rem',
    alignItems: 'center',
    padding: '0 1.25rem',
    borderBottom: '1px solid #8fb3d1',
    backgroundColor: '#d9e8f5',
    boxShadow: '0 3px 14px rgba(0, 36, 77, 0.06)',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 20,
});

const MainGrid = styled('main', {
    height: '100vh',
    paddingTop: HEADER_HEIGHT,
    paddingBottom: FOOTER_HEIGHT,
    boxSizing: 'border-box',
    overflow: 'hidden',
});

const SideNav = styled('aside', {
    position: 'fixed',
    top: HEADER_HEIGHT,
    left: 0,
    bottom: FOOTER_HEIGHT,
    width: SIDENAV_WIDTH,
    borderRight: '1px solid #7fa6c7',
    padding: 0,
    backgroundColor: '#cfe1f1',
    boxShadow: 'inset -1px 0 0 #7fa6c7',
    overflowY: 'auto',
    '& nav, & ul, & li': {
        marginRight: 0,
        paddingRight: 0,
    },
    '& a, & button': {
        whiteSpace: 'nowrap',
        paddingRight: '0.25rem',
    },
    '& .abyss-sub-navigation-menu-link-container': {
        transition: 'background-color 120ms ease',
    },
    '& .abyss-sub-navigation-menu-link-container:hover': {
        backgroundColor: 'rgba(0, 57, 128, 0.10)',
    },
    // Keep active indicator on leaf links only; prevent duplicate bar on dropdown parents.
    '& .abyss-sub-navigation-dropdown-wrapper:before': {
        display: 'none !important',
    },
    transition: 'width 200ms ease, padding 200ms ease, border-right-width 200ms ease',
    willChange: 'width',
});

const Content = styled('section', {
    height: `calc(100vh - ${HEADER_HEIGHT} - ${FOOTER_HEIGHT})`,
    minHeight: 0,
    width: `calc(100% - ${SIDENAV_WIDTH})`,
    marginLeft: SIDENAV_WIDTH,
    paddingTop: '1.5rem',
    paddingRight: '1.5rem',
    paddingBottom: '1.5rem',
    paddingLeft: '1.5rem',
    boxSizing: 'border-box',
    overflowY: 'auto',
    overflowX: 'hidden',
    overscrollBehavior: 'contain',
    transition: 'width 200ms ease, margin-left 200ms ease',
    willChange: 'width, margin-left',
});

const FooterText = styled(Text, {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: FOOTER_HEIGHT,
    margin: 0,
    padding: '0.25rem 0.75rem',
});

const Footer = styled(AbyssFooter, {
    width: '100%',
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 20,
    boxShadow: '0 -3px 12px rgba(0, 36, 77, 0.12)',
    '& .abyss-footer-container': {
        backgroundColor: '#d9e8f5',
        borderTop: '1px solid #8fb3d1',
        paddingTop: '0',
        paddingRight: '0',
        paddingBottom: '0',
        paddingLeft: '0',
        gap: '0',
    },
});

const HeaderBrand = styled('div', {
    display: 'flex',
    alignItems: 'center',
    justifySelf: 'start',
    columnGap: '0.875rem',
    maxWidth: '100%',
    minWidth: 0,
    overflow: 'hidden',
});

const BrandmarkLink = styled('a', {
    display: 'inline-flex',
    alignItems: 'center',
    textDecoration: 'none',
    cursor: 'pointer',
    '&:focus-visible': {
        outline: '2px solid $core.color.brand.80',
        outlineOffset: '2px',
    },
});

const HeaderTitle = styled(Heading, {
    margin: 0,
    justifySelf: 'center',
    width: '100%',
    padding: '0 0.5rem',
    fontSize: 'clamp(0.8rem, 1.55vw, 1.35rem)',
    lineHeight: 1.2,
    textAlign: 'center',
    whiteSpace: 'nowrap',
});

const HeaderActions = styled(Layout.Group, {
    alignItems: 'center',
    justifySelf: 'end',
    columnGap: '0.75rem',
    maxWidth: '100%',
    minWidth: 0,
    overflow: 'hidden',
    flexWrap: 'nowrap',
    whiteSpace: 'nowrap',
    transformOrigin: 'right center',
    '@media (max-width: 1100px)': {
        columnGap: '0.5rem',
        transform: 'scale(0.95)',
    },
    '@media (max-width: 900px)': {
        columnGap: '0.375rem',
        transform: 'scale(0.85)',
    },
    '@media (max-width: 760px)': {
        transform: 'scale(0.78)',
    },
});

const UserName = styled(Text, {
    color: '$gray11',
    fontSize: 'clamp(0.68rem, 1.05vw, 0.9rem)',
    maxWidth: 'clamp(80px, 15vw, 200px)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
});

const CollapsedNavigation = styled('nav', {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.25rem',
    paddingTop: '0.5rem',
});

const CollapsedNavButton = styled(Button, {
    position: 'relative',
    width: '48px',
    minWidth: '48px',
    height: '44px',
    padding: 0,
    borderRadius: 0,
    '&[data-active="true"]:before': {
        content: '""',
        position: 'absolute',
        top: '6px',
        bottom: '6px',
        left: 0,
        width: '3px',
        backgroundColor: '$core.color.brand.80',
    },
});

type SideNavItem = {
    title: string;
    href?: string;
    icon: string;
    subItems?: SideNavItem[];
};

interface CollapsedSideNavProps {
    navItems: SideNavItem[];
    isActiveNavItem: (item: SideNavItem) => boolean;
    isAdminExpanded: boolean;
    onAdminExpandedChange: () => void;
}

const CollapsedSideNav = ({
    navItems,
    isActiveNavItem,
    isAdminExpanded,
    onAdminExpandedChange,
}: CollapsedSideNavProps) => {
    const renderNavigationButton = (item: SideNavItem, isSubItem = false) => {
        const isDropdown = item.subItems && item.subItems.length > 0;
        const isActive = isActiveNavItem(item);

        return (
            <Tooltip
                key={item.title}
                content={item.title}
                position="right"
                variant="gray"
                delayDuration={0}
            >
                <CollapsedNavButton
                    aria-label={item.title}
                    aria-expanded={isDropdown ? isAdminExpanded : undefined}
                    data-active={isActive}
                    icon={{ icon: item.icon, position: 'icon-only' }}
                    variant="text"
                    color="neutral"
                    size="sm"
                    onClick={() => {
                        if (isDropdown) {
                            onAdminExpandedChange();
                            return;
                        }

                        if (item.href) {
                            navigate(item.href);
                        }
                    }}
                    css={isSubItem ? { marginTop: '-0.25rem' } : undefined}
                />
            </Tooltip>
        );
    };

    return (
        <CollapsedNavigation aria-label="Collapsed navigation" data-tour={UI_KEYS.HOME.DOM.TOUR_SIDEBAR}>
            {navItems.flatMap((item) => {
                const itemButtons = [renderNavigationButton(item)];
                if (item.subItems && isAdminExpanded) {
                    itemButtons.push(...item.subItems.map((subItem) => {return renderNavigationButton(subItem, true)}));
                }
                return itemButtons;
            })}
        </CollapsedNavigation>
    );
};

interface HeaderBarProps {
    displayName: string;
    onLogout: () => void;
}

const HeaderBar = ({ displayName, onLogout }: HeaderBarProps) => {
    return (
        <Header>
            <HeaderBrand>
                <BrandmarkLink
                    href="/home"
                    aria-label="Go to Home"
                    onClick={(event) => {
                        event.preventDefault();
                        navigate('/home');
                    }}
                >
                    <Brandmark
                        size="40%"
                        affiliate="uhc"
                        variant="lockup"
                        color="blue"
                        brand="uhc"
                    />
                </BrandmarkLink>
            </HeaderBrand>
            <HeaderTitle
                level={3}
                size="lg"
                color="$web.semantic.color.text.content.primary"
                textAlign="center"
            >
                UnitedHealthcare Request for Information (RFI)
            </HeaderTitle>
            <HeaderActions>
                <UserName>{displayName}</UserName>
                <Button
                    onClick={onLogout}
                    variant="text"
                    color="neutral"
                    size="sm"
                    icon={{ icon: 'logout', position: 'leading' }}
                >
                    Logout
                </Button>
            </HeaderActions>
        </Header>
    );
};

export const AppBody = () => {
    const { session, logout } = useAuth();
    const { getLocation } = useRouter();
    const currentLocation = getLocation?.();
    const [hoveredPath, setHoveredPath] = React.useState<string | null>(null);

    const normalizePath = React.useCallback((path?: string | null) => {
        if (!path) {
            return '/home';
        }

        if (path.length > 1 && path.endsWith('/')) {
            return path.slice(0, -1);
        }

        return path;
    }, []);

    const currentPath = normalizePath(currentLocation?.pathname);

    const getNormalizedHrefPath = React.useCallback((href: string) => {
        try {
            if (href.startsWith('http://') || href.startsWith('https://')) {
                return normalizePath(new URL(href).pathname);
            }
            return normalizePath(href);
        } catch {
            return normalizePath(href);
        }
    }, [normalizePath]);
    const navItems = React.useMemo(
        () => {return session?.isAdmin
            ? [...BASE_SIDE_NAV_ITEMS, ADMIN_SIDE_NAV_ITEM]
            : BASE_SIDE_NAV_ITEMS},
        [session],
    );
    const [isSideNavExpanded, setIsSideNavExpanded] = React.useState(true);
    const [isSideNavHoverEnabled, setIsSideNavHoverEnabled] = React.useState(false);
    const [isCollapsedAdminExpanded, setIsCollapsedAdminExpanded] = React.useState(false);
    const sideNavHoverTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearSideNavHoverTimer = React.useCallback(() => {
        if (sideNavHoverTimerRef.current) {
            clearTimeout(sideNavHoverTimerRef.current);
            sideNavHoverTimerRef.current = null;
        }
    }, []);

    const scheduleSideNavState = React.useCallback((expanded: boolean, delayMs: number) => {
        clearSideNavHoverTimer();
        sideNavHoverTimerRef.current = setTimeout(() => {
            setIsSideNavExpanded(expanded);
            sideNavHoverTimerRef.current = null;
        }, delayMs);
    }, [clearSideNavHoverTimer]);

    React.useEffect(() => {
        return () => {
            clearSideNavHoverTimer();
        };
    }, [clearSideNavHoverTimer]);

    React.useEffect(() => {
        return subscribeSideNavCollapse(() => {
            clearSideNavHoverTimer();
            setIsSideNavExpanded(false);
        });
    }, [clearSideNavHoverTimer]);

    const isActiveNavItem = React.useCallback((item: { href?: string; subItems?: Array<{ href?: string }> }) => {
        const selectedPath = hoveredPath ?? currentPath;

        if (item.href) {
            return normalizePath(item.href) === selectedPath;
        }

        return item.subItems?.some((subItem) => {return normalizePath(subItem.href) === selectedPath}) ?? false;
    }, [currentPath, hoveredPath, normalizePath]);

    return (
        <Shell>
            <HeaderBar
                displayName={session?.displayName ?? 'Authenticated User'}
                onLogout={logout}
            />

            <MainGrid>
                <SideNav
                    onClickCapture={(event) => {
                        const target = event.target as HTMLElement | null;
                        const dropdownButton = target?.closest('.abyss-sub-navigation-dropdown-button');
                        if (dropdownButton && event.detail > 0) {
                            event.preventDefault();
                            event.stopPropagation();
                            const button = dropdownButton as HTMLElement;
                            button.focus();
                            window.setTimeout(() => {
                                button.click();
                            }, 0);
                        }
                    }}
                    onMouseOver={(event) => {
                        const target = event.target as HTMLElement | null;

                        // Auto-expand dropdown on hover
                        const dropdownWrapper = target?.closest('.abyss-sub-navigation-dropdown-wrapper') as HTMLElement | null;
                        if (dropdownWrapper) {
                            const expandBtn = dropdownWrapper.querySelector('.abyss-sub-navigation-dropdown-button') as HTMLElement | null;
                            if (expandBtn && expandBtn.getAttribute('aria-expanded') === 'false') {
                                expandBtn.click();
                            }
                        }

                        // Hover active-bar movement
                        const rowContainer = target?.closest('.abyss-sub-navigation-menu-link-container') as HTMLElement | null;
                        const link = rowContainer?.querySelector('[sub-nav-menu-item="true"]') as HTMLAnchorElement | null;
                        const href = link?.getAttribute('href');
                        if (!href) return;
                        setHoveredPath(getNormalizedHrefPath(href));
                    }}
                    onMouseLeave={() => {
                        setHoveredPath(null);
                    }}
                    onMouseEnter={() => {
                        if (isSideNavHoverEnabled) {
                            scheduleSideNavState(true, SIDENAV_SHOW_DELAY_MS);
                        }
                    }}
                    css={isSideNavExpanded ? undefined : {
                        width: SIDENAV_COLLAPSED_WIDTH,
                        padding: 0,
                        overflowY: 'auto',
                    }}
                >
                    {isSideNavExpanded ? (
                        <V1SubNavigationMenu
                            title="Navigation"
                            variant="sideBarMenu"
                            position="left"
                            width="100%"
                            navItems={navItems}
                            setActive={isActiveNavItem}
                        />
                    ) : (
                        <CollapsedSideNav
                            navItems={navItems}
                            isActiveNavItem={isActiveNavItem}
                            isAdminExpanded={isCollapsedAdminExpanded}
                            onAdminExpandedChange={() => {
                                setIsCollapsedAdminExpanded((isExpanded) => {return !isExpanded});
                            }}
                        />
                    )}
                </SideNav>

                <Content
                    onClick={() => {
                        setIsSideNavHoverEnabled(true);
                    }}
                    onMouseEnter={() => {
                        if (isSideNavHoverEnabled) {
                            scheduleSideNavState(false, SIDENAV_HIDE_DELAY_MS);
                        }
                    }}
                    css={isSideNavExpanded ? undefined : {
                        marginLeft: SIDENAV_COLLAPSED_WIDTH,
                        width: `calc(100% - ${SIDENAV_COLLAPSED_WIDTH})`,
                    }}
                >
                    <Router.Outlet />
                </Content>
            </MainGrid>

            <Footer fullWidth variant="alt">
                <FooterText
                    fontSize="md"
                    color="$web.semantic.color.text.content.primary"
                >
                    @2025 United Healthcare Services, Inc. All Rights Reserved
                </FooterText>
            </Footer>
        </Shell>
    );
};
